import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, FileText, Download } from "lucide-react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import Seo from "../components/Seo";
import AttachmentLink from "../components/AttachmentLink";

// Convert plain text with markdown-ish links to clickable list
function renderContent(text) {
  if (!text) return null;
  // Split into paragraphs by blank line
  const paragraphs = text.split(/\n{2,}/);
  return paragraphs.map((para, i) => {
    // Heading (**text**)
    if (/^\*\*(.+)\*\*$/.test(para.trim())) {
      return (
        <h3 key={i} className="font-heading font-extrabold text-ink-900 text-xl md:text-2xl mt-8 mb-3 tracking-tight">
          {para.trim().replace(/^\*\*|\*\*$/g, "")}
        </h3>
      );
    }
    // Horizontal rule
    if (para.trim() === "---") {
      return <hr key={i} className="hr-thick my-8" />;
    }
    // Bullet list
    if (/^[•·]/m.test(para.trim())) {
      const items = para.split(/\n/).filter((l) => /^[•·]/.test(l.trim()));
      return (
        <ul key={i} className="my-4 space-y-2">
          {items.map((it, j) => {
            const clean = it.replace(/^[•·]\s*/, "");
            return (
              <li key={j} className="flex items-start gap-3 text-ink-700">
                <span className="text-green-700 mt-1.5 flex-shrink-0">▸</span>
                <span className="leading-relaxed">{renderInline(clean)}</span>
              </li>
            );
          })}
        </ul>
      );
    }
    // Numbered list
    if (/^\d+\.\s/m.test(para.trim())) {
      const items = para.split(/\n/).filter((l) => /^\d+\.\s/.test(l.trim()));
      return (
        <ol key={i} className="my-4 space-y-2 list-decimal pl-6">
          {items.map((it, j) => (
            <li key={j} className="text-ink-700 leading-relaxed pl-1">
              {renderInline(it.replace(/^\d+\.\s*/, ""))}
            </li>
          ))}
        </ol>
      );
    }
    return (
      <p key={i} className="my-4 text-ink-700 leading-relaxed">
        {renderInline(para)}
      </p>
    );
  });
}

function renderInline(text) {
  // Process **bold** and URLs
  const parts = [];
  const regex = /(\*\*[^*]+\*\*|https?:\/\/[^\s]+|mailto:[^\s]+)/g;
  let lastIndex = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    const m = match[0];
    if (m.startsWith("**") && m.endsWith("**")) {
      parts.push(<strong key={match.index} className="font-semibold text-ink-900">{m.slice(2, -2)}</strong>);
    } else {
      const href = m;
      const label = href.length > 60 ? href.slice(0, 60) + "…" : href;
      parts.push(
        <a key={match.index} href={href} target="_blank" rel="noopener noreferrer"
           className="text-green-700 hover:text-green-800 break-all font-mono text-[13px] underline-offset-2 hover:underline">
          {label}
        </a>
      );
    }
    lastIndex = match.index + m.length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}

export default function InfoPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setPage(null);
    setError("");
    api.get(`/info-pages/${slug}`)
      .then((r) => setPage(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Stránka sa nenašla."));
  }, [slug]);

  if (error) {
    return (
      <div>
        <PageHeader
          eyebrow="Chyba"
          title="Stránka nenájdená"
          breadcrumbs={[{ label: "Stránka nenájdená" }]}
        />
        <div className="wrap py-12 text-center">
          <p className="text-ink-500 mb-6">{error}</p>
          <Link to="/" className="btn btn-dark"><ArrowLeft className="w-4 h-4" /> Späť na úvod</Link>
        </div>
      </div>
    );
  }

  if (!page) {
    return <div className="wrap py-20 text-ink-500">Načítavam...</div>;
  }

  return (
    <div data-testid={`info-page-${slug}`}>
      <Seo title={page.title} description={page.subtitle || `${page.title} — ObFZ Levice.`} />
      <PageHeader
        eyebrow="Informačná stránka"
        title={page.title}
        description={page.subtitle}
        breadcrumbs={[{ label: page.title }]}
      >
        {page.external_link && (
          <a href={page.external_link} target="_blank" rel="noopener noreferrer" className="btn btn-outline-white">
            Pôvodný zdroj <ArrowUpRight className="w-4 h-4" />
          </a>
        )}
      </PageHeader>

      <section className="section-pad bg-paper">
        <div className="wrap max-w-4xl">
          <article className="bg-white border border-ink-100 p-8 md:p-12">
            <div className="prose-content">
              {renderContent(page.content)}
            </div>
            {page.attachment_url && (
              <div className="mt-8 pt-6 border-t border-ink-100">
                <div className="mono text-[11px] font-bold uppercase tracking-widest text-ink-500 mb-3">Príloha</div>
                <AttachmentLink url={page.attachment_url} name={page.attachment_name} />
              </div>
            )}
          </article>

          <div className="mt-8 text-center">
            <Link to="/" className="btn btn-ghost"><ArrowLeft className="w-4 h-4" /> Späť na úvod</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
